import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaUser, 
  FaCode, 
  FaAward,
  FaLaptopCode,
  FaGlobe,
  FaHeart,
  FaBuilding,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCertificate,
  FaLaptop,
  FaClock,
  FaCheckCircle,
  FaUsers,
  FaQuoteLeft,
  FaStar,
  FaThumbsUp,
  FaFileAlt,
  FaHandshake
} from 'react-icons/fa';
import SEO from '../components/SEO';

// Import certificates from assets
import hsnCertificate from '../assets/certificates/HSN Internship Certificate.jpg';
import combineFoundationCertificate from '../assets/certificates/Combine Foundation Certificate.jpg';
import codeAlphaCertificate from '../assets/certificates/CodeAlpha Internship Certificate.jpg';

// Import recommendation letters from assets
import hsnRecommendation from '../assets/recommendations/HSN Design Studio Letter Of Recommendation.jpg';
import codeAlphaRecommendation from '../assets/recommendations/Code Alpha Letter Of Recommendation.jpg';

// Import offer letters from assets
import hsnOfferLetter from '../assets/offer-letters/HSN Design Studio Internship Offer Letter.jpg';
import codeAlphaOfferLetter from '../assets/offer-letters/Code Alpha Internship Offer Letter.jpg';

const About: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const experiences = [
    {
      title: 'Full Stack Developer',
      company: 'Self-Employed / Freelance',
      period: '2021 - Present',
      description: 'Building scalable web applications using React, Node.js, and PHP/MySQL. Delivering end-to-end solutions with modern technologies.',
      color: 'primary'
    },
    {
      title: 'Amazon Virtual Assistant (V.A)',
      company: 'Remote',
      period: '2022 - 2023',
      description: 'End-to-end Amazon account management specializing in FBA, FBM, and listing optimization. Helped clients grow their Amazon businesses.',
      color: 'secondary'
    },
    {
      title: 'MS Office Specialist',
      company: 'Professional Services',
      period: '2020 - 2021',
      description: 'Professional MS Office solutions for accurate, organized, and impactful work. Expertise in Word, Excel, and PowerPoint.',
      color: 'primary'
    },
  ];

  // Certificates Data - Ordered by date (oldest to newest)
  const certificates = [
    {
      id: 1,
      title: 'Web Development & Design Internship',
      organization: 'HSN Design Studio',
      type: 'Onsite Internship',
      duration: '3 Months',
      period: 'Jun 20, 2025 to Sept 20, 2025',
      location: 'Onsite',
      certificateId: null,
      icon: FaBuilding,
      bgColor: 'from-purple-50 to-pink-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-600',
      image: hsnCertificate,
      supervisor: 'Engr. Muhammad Hassan Memon (Founder)',
      details: [
        'Compulsory internship program completion',
        'Department of Web Development and Design',
        'Worked under Head Supervisor Engr. Muhammad Hassan Memon',
        'Hands-on experience with real-world projects',
        'Collaborated with design and development teams'
      ]
    },
    {
      id: 2,
      title: 'Youth Leadership Program Webinar',
      organization: 'Combine Foundation',
      type: 'Webinar / Online',
      duration: '1 Day',
      period: '8th March 2026',
      location: 'Online',
      certificateId: 'CF/HRM/26/YLP/S766',
      icon: FaUsers,
      bgColor: 'from-green-50 to-emerald-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-600',
      image: combineFoundationCertificate,
      details: [
        '"Zero to Full Stack Developer - A Complete Industry Roadmap"',
        'Presented by M. Subhan Khan & Zohaib Ali',
        'Hosted by Javeria Anis',
        'Recognition of Outstanding Dedication',
        'Active Engagement and Meaningful Contribution Throughout the Session'
      ]
    },
    {
      id: 3,
      title: 'Full Stack Development Internship',
      organization: 'CodeAlpha',
      type: 'Virtual Internship',
      duration: '1 Month',
      period: '20th March 2026 to 20th April 2026',
      location: 'Remote',
      certificateId: 'CA/DF1/37244',
      icon: FaLaptop,
      bgColor: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-600',
      image: codeAlphaCertificate,
      details: [
        'Successfully completed Virtual Internship Program',
        'Full Stack Development track',
        'Demonstrated excellent analytical skills',
        'Quickly acquired new skills and adapted to emerging technologies',
        'Consistently displayed willingness to assist team members'
      ]
    }
  ];

  // Recommendations Data
  const recommendations = [
    {
      id: 1,
      name: 'Engr. Muhammad Hassan Memon',
      title: 'CEO, HSN Design Studio (Private) Limited',
      relationship: 'Supervisor',
      date: '20th September 2025',
      rating: 5,
      text: 'Sarfraz Ahmed demonstrated remarkable talent, dedication, and a genuine passion for Web Designing & Development during his internship at HSN Design Studio. His exceptional performance and contribution to our team make him a valuable asset to any organization.',
      image: hsnRecommendation,
      icon: FaBuilding,
      bgColor: 'from-purple-50 to-pink-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-600'
    },
    {
      id: 2,
      name: 'Swati Srivastava',
      title: 'Founder & CEO, CodeAlpha',
      relationship: 'Supervisor',
      date: '21st April 2026',
      rating: 5,
      text: 'Sarfraz Ahmed has excellent analytical skills and quickly acquired new skills while adeptly adapting to emerging technologies. His dedication, willingness to assist, and strong collaboration with team members make him a valuable asset to any prospective employer.',
      image: codeAlphaRecommendation,
      icon: FaLaptop,
      bgColor: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-600'
    }
  ];

  // Offer Letters Data
  const offerLetters = [
    {
      id: 1,
      title: 'Web Development & Design Internship',
      organization: 'HSN Design Studio',
      type: 'Offer Letter',
      period: 'Jun 20, 2025 to Sept 20, 2025',
      location: 'Onsite',
      recipient: 'Sarfraz Ahmed Jamali',
      issuer: 'Engr. M. Hassan Memon (CEO)',
      icon: FaHandshake,
      bgColor: 'from-orange-50 to-amber-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-600',
      image: hsnOfferLetter,
      details: [
        'Creative and collaborative work environment',
        'Opportunity to work on exciting projects',
        'Learn from experienced Web Designing & Development professionals',
        'Mentoring program for aspiring designers',
        'One vacation day per month policy'
      ]
    },
    {
      id: 2,
      title: 'Full Stack Development Internship',
      organization: 'CodeAlpha',
      type: 'Offer Letter',
      period: '20th March 2026 to 20th April 2026',
      location: 'Remote',
      recipient: 'Sarfraz Ahmed',
      issuer: 'Swati Srivastava (Founder & CEO)',
      icon: FaFileAlt,
      bgColor: 'from-cyan-50 to-sky-50',
      borderColor: 'border-cyan-200',
      textColor: 'text-cyan-600',
      image: codeAlphaOfferLetter,
      details: [
        'Learning opportunity with hands-on application',
        'Orientation and emphasis on learning new skills',
        'Deeper understanding of concepts through practical work',
        'Worthwhile and fruitful association',
        'Equipped for future projects'
      ]
    }
  ];

  const achievements = [
    { icon: FaAward, label: 'Projects Completed', value: '25+' },
    { icon: FaCode, label: 'Years Experience', value: '3+' },
    { icon: FaGlobe, label: 'Happy Clients', value: '15+' },
    { icon: FaHeart, label: 'Client Satisfaction', value: '100%' },
  ];

  return (
    <>
      <SEO 
        title="About" 
        description="Learn about Sarfraz Ahmed - Professional Full Stack Developer with 3+ years of experience in MERN Stack, PHP, and MySQL" 
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 pt-24">
        {/* About Header */}
        <section className="py-12">
          <div className="container-custom px-4">
            <motion.div
              ref={ref}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-4 py-1 bg-primary/10 rounded-full text-primary text-sm font-medium mb-4">
                About Me
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-primary mb-4">
                Know Me <span className="text-gray-800 dark:text-gray-100">Better</span>
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full" />
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Left Column */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-6"
              >
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300">
                  <h2 className="text-2xl font-display font-bold text-gray-800 dark:text-gray-100 mb-4">
                    Who I Am & What I Do
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                    I'm a passionate Full-Stack Developer with expertise in building end-to-end web applications. 
                    My journey spans from crafting pixel-perfect frontends with React and Tailwind CSS to 
                    architecting robust backends with Node.js, Express.js, and PHP.
                  </p>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    I specialize in creating scalable solutions that handle both NoSQL (MongoDB) and SQL (MySQL) 
                    databases, implementing secure authentication systems, and deploying applications to cloud 
                    platforms. My focus is on writing clean, maintainable code that follows industry best practices.
                  </p>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FaUser className="text-primary text-xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Personal Info</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500">Full Name</p>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Sarfraz Ahmed</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500">Experience</p>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">3+ Years</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500">Location</p>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Pakistan</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500">Availability</p>
                          <p className="text-sm font-medium text-green-500">Open to work</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Right Column */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="space-y-6"
              >
                <div className="grid grid-cols-2 gap-4">
                  {achievements.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={inView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 text-center"
                    >
                      <item.icon className="text-3xl text-primary mx-auto mb-2" />
                      <div className="text-xl font-bold gradient-text">{item.value}</div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">{item.label}</div>
                    </motion.div>
                  ))}
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-secondary/10 rounded-xl flex items-center justify-center">
                      <FaLaptopCode className="text-secondary text-xl" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">My Approach</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-300">Full-stack development with MERN Stack & PHP/MySQL</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-secondary rounded-full mt-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-300">Scalable, secure, and performant applications</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-300">Clean, maintainable code with best practices</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-secondary rounded-full mt-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-300">Cloud deployment and DevOps integration</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Certificates Section */}
        <section id="certificates" className="py-16 bg-white/80 dark:bg-gray-900/80">
          <div className="container-custom px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="inline-block px-4 py-1 bg-secondary/10 rounded-full text-secondary text-sm font-medium mb-4">
                My Certificates
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-primary mb-3">
                Professional <span className="text-gray-800 dark:text-gray-100">Certifications</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full" />
              <p className="text-gray-600 dark:text-gray-300 mt-4 max-w-2xl mx-auto">
                Industry-recognized certificates from leading organizations and training programs
              </p>
            </motion.div>

            <div className="max-w-6xl mx-auto space-y-8">
              {certificates.map((cert, index) => (
                <motion.div
                  key={cert.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-br ${cert.bgColor} rounded-2xl shadow-lg overflow-hidden border ${cert.borderColor} transition-all duration-300 hover:shadow-2xl`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Certificate Image */}
                    <div className="md:w-1/2 lg:w-2/5 bg-gray-100 dark:bg-gray-800 flex items-center justify-center p-4">
                      <div className="relative w-full max-w-md mx-auto">
                        <img
                          src={cert.image}
                          alt={`${cert.organization} Certificate`}
                          className="w-full h-auto object-contain rounded-lg shadow-md"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              const placeholder = document.createElement('div');
                              placeholder.className = 'w-full aspect-[4/3] flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg';
                              placeholder.innerHTML = `
                                <div class="text-center p-6">
                                  <div class="text-6xl mb-3">📜</div>
                                  <p class="text-lg font-semibold text-gray-700 dark:text-gray-200">${cert.organization}</p>
                                  <p class="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">Certificate</p>
                                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-2">${cert.title}</p>
                                </div>
                              `;
                              parent.appendChild(placeholder);
                            }
                          }}
                        />
                        <div className="absolute top-2 right-2 flex flex-col gap-1">
                          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-700 dark:text-gray-200 shadow-md">
                            {cert.type}
                          </span>
                          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-700 dark:text-gray-200 shadow-md">
                            {cert.duration}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Certificate Details */}
                    <div className="md:w-1/2 lg:w-3/5 p-6 md:p-8">
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`w-12 h-12 ${cert.textColor} bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-md`}>
                          <cert.icon className="text-2xl" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                            {cert.organization}
                          </h3>
                          <p className="text-gray-700 dark:text-gray-200 font-medium">{cert.title}</p>
                        </div>
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-white/60 rounded-lg px-3 py-2">
                          <FaCalendarAlt className={`${cert.textColor} flex-shrink-0`} />
                          <span className="truncate">{cert.period}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-white/60 rounded-lg px-3 py-2">
                          <FaClock className={`${cert.textColor} flex-shrink-0`} />
                          <span>Duration: {cert.duration}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-white/60 rounded-lg px-3 py-2">
                          <FaMapMarkerAlt className={`${cert.textColor} flex-shrink-0`} />
                          <span>{cert.location}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-white/60 rounded-lg px-3 py-2">
                          <FaCertificate className={`${cert.textColor} flex-shrink-0`} />
                          <span className="truncate">
                            {cert.certificateId ? `ID: ${cert.certificateId}` : 'No ID'}
                          </span>
                        </div>
                      </div>

                      {/* HSN Supervisor Info */}
                      {cert.supervisor && (
                        <div className="mb-4 p-3 bg-white/60 rounded-lg border border-gray-200 dark:border-gray-700/60">
                          <p className="text-sm text-gray-700 dark:text-gray-200">
                            <span className="font-semibold">Supervisor:</span> {cert.supervisor}
                          </p>
                        </div>
                      )}

                      {/* Key Highlights */}
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700/60">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                          <FaCheckCircle className="text-green-500" />
                          Key Highlights:
                        </h4>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {cert.details.map((detail, idx) => (
                            <li key={idx} className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2 bg-white/40 rounded-lg px-3 py-1.5">
                              <span className="text-green-500 mt-0.5">•</span>
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Letters of Recommendation Section */}
        <section id="recommendations" className="py-16 bg-white/50 dark:bg-gray-900/50">
          <div className="container-custom px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="inline-block px-4 py-1 bg-primary/10 rounded-full text-primary text-sm font-medium mb-4">
                Testimonials
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-primary mb-3">
                Letters of <span className="text-gray-800 dark:text-gray-100">Recommendation</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full" />
              <p className="text-gray-600 dark:text-gray-300 mt-4 max-w-2xl mx-auto">
                What professionals say about my work ethic, skills, and contributions
              </p>
            </motion.div>

            <div className="max-w-6xl mx-auto space-y-8">
              {recommendations.map((rec, index) => (
                <motion.div
                  key={rec.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-br ${rec.bgColor} rounded-2xl shadow-lg overflow-hidden border ${rec.borderColor} transition-all duration-300 hover:shadow-2xl`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Recommendation Image */}
                    <div className="md:w-1/2 lg:w-2/5 bg-gray-100 dark:bg-gray-800 flex items-center justify-center p-4">
                      <div className="relative w-full max-w-md mx-auto">
                        <img
                          src={rec.image}
                          alt={`Recommendation from ${rec.name}`}
                          className="w-full h-auto object-contain rounded-lg shadow-md"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              const placeholder = document.createElement('div');
                              placeholder.className = 'w-full aspect-[4/3] flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg';
                              placeholder.innerHTML = `
                                <div class="text-center p-6">
                                  <div class="text-6xl mb-3">📝</div>
                                  <p class="text-lg font-semibold text-gray-700 dark:text-gray-200">${rec.name}</p>
                                  <p class="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">Recommendation Letter</p>
                                </div>
                              `;
                              parent.appendChild(placeholder);
                            }
                          }}
                        />
                        <div className="absolute top-2 right-2 flex flex-col gap-1">
                          <div className="flex items-center gap-1 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md">
                            <FaStar className="text-yellow-500 text-xs" />
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">{rec.rating}/5</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Recommendation Details */}
                    <div className="md:w-1/2 lg:w-3/5 p-6 md:p-8">
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`w-12 h-12 ${rec.textColor} bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-md`}>
                          <FaQuoteLeft className="text-2xl" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                            {rec.name}
                          </h3>
                          <p className="text-gray-700 dark:text-gray-200 font-medium">{rec.title}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">{rec.relationship}</p>
                        </div>
                      </div>

                      {/* Rating Stars */}
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <FaStar
                            key={i}
                            className={`${i < rec.rating ? 'text-yellow-500' : 'text-gray-300'} text-sm`}
                          />
                        ))}
                        <span className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 ml-2">{rec.date}</span>
                      </div>

                      {/* Recommendation Text */}
                      <div className="mb-4 p-4 bg-white/60 rounded-lg border border-gray-200 dark:border-gray-700/60">
                        <p className="text-gray-700 dark:text-gray-200 text-sm leading-relaxed italic">
                          "{rec.text}"
                        </p>
                      </div>

                      {/* Footer Info */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700/60">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 ${rec.textColor} bg-white/60 rounded-lg flex items-center justify-center`}>
                            <rec.icon className="text-sm" />
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">Recommendation</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaThumbsUp className="text-green-500" />
                          <span className="text-sm text-gray-600 dark:text-gray-300">Verified</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Offer Letters Section */}
        <section className="py-16 bg-white/80 dark:bg-gray-900/80">
          <div className="container-custom px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="inline-block px-4 py-1 bg-secondary/10 rounded-full text-secondary text-sm font-medium mb-4">
                Offers
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-primary mb-3">
                Internship <span className="text-gray-800 dark:text-gray-100">Offer Letters</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full" />
              <p className="text-gray-600 dark:text-gray-300 mt-4 max-w-2xl mx-auto">
                Official internship offer letters from leading organizations
              </p>
            </motion.div>

            <div className="max-w-6xl mx-auto space-y-8">
              {offerLetters.map((offer, index) => (
                <motion.div
                  key={offer.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-br ${offer.bgColor} rounded-2xl shadow-lg overflow-hidden border ${offer.borderColor} transition-all duration-300 hover:shadow-2xl`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Offer Letter Image */}
                    <div className="md:w-1/2 lg:w-2/5 bg-gray-100 dark:bg-gray-800 flex items-center justify-center p-4">
                      <div className="relative w-full max-w-md mx-auto">
                        <img
                          src={offer.image}
                          alt={`${offer.organization} Offer Letter`}
                          className="w-full h-auto object-contain rounded-lg shadow-md"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              const placeholder = document.createElement('div');
                              placeholder.className = 'w-full aspect-[4/3] flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg';
                              placeholder.innerHTML = `
                                <div class="text-center p-6">
                                  <div class="text-6xl mb-3">📄</div>
                                  <p class="text-lg font-semibold text-gray-700 dark:text-gray-200">${offer.organization}</p>
                                  <p class="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">Offer Letter</p>
                                </div>
                              `;
                              parent.appendChild(placeholder);
                            }
                          }}
                        />
                        <div className="absolute top-2 right-2 flex flex-col gap-1">
                          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-700 dark:text-gray-200 shadow-md">
                            {offer.type}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Offer Letter Details */}
                    <div className="md:w-1/2 lg:w-3/5 p-6 md:p-8">
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`w-12 h-12 ${offer.textColor} bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-md`}>
                          <offer.icon className="text-2xl" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                            {offer.organization}
                          </h3>
                          <p className="text-gray-700 dark:text-gray-200 font-medium">{offer.title}</p>
                        </div>
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-white/60 rounded-lg px-3 py-2">
                          <FaCalendarAlt className={`${offer.textColor} flex-shrink-0`} />
                          <span className="truncate">{offer.period}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-white/60 rounded-lg px-3 py-2">
                          <FaMapMarkerAlt className={`${offer.textColor} flex-shrink-0`} />
                          <span>{offer.location}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-white/60 rounded-lg px-3 py-2">
                          <FaUser className={`${offer.textColor} flex-shrink-0`} />
                          <span className="truncate">To: {offer.recipient}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-white/60 rounded-lg px-3 py-2">
                          <FaBuilding className={`${offer.textColor} flex-shrink-0`} />
                          <span className="truncate">From: {offer.issuer}</span>
                        </div>
                      </div>

                      {/* Key Highlights */}
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700/60">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                          <FaCheckCircle className="text-green-500" />
                          Key Highlights:
                        </h4>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {offer.details.map((detail, idx) => (
                            <li key={idx} className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2 bg-white/40 rounded-lg px-3 py-1.5">
                              <span className="text-green-500 mt-0.5">•</span>
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Experience Section */}
        <section id="experience" className="py-16 bg-white/50 dark:bg-gray-900/50">
          <div className="container-custom px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold text-primary mb-3">
                Work <span className="text-gray-800 dark:text-gray-100">Experience</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full" />
            </motion.div>

            <div className="max-w-4xl mx-auto">
              {experiences.map((exp, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="relative pl-8 pb-12 last:pb-0"
                >
                  {index < experiences.length - 1 && (
                    <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-gradient-to-b from-primary to-secondary" />
                  )}
                  <div className="absolute left-0 top-2 w-6 h-6 bg-white rounded-full border-2 border-primary flex items-center justify-center">
                    <div className="w-2 h-2 bg-primary rounded-full" />
                  </div>
                  
                  <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-shadow duration-300 ml-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                      <h3 className="text-xl font-display font-bold text-gray-800 dark:text-gray-100">{exp.title}</h3>
                      <span className="text-sm text-primary font-medium">{exp.period}</span>
                    </div>
                    <div className="text-secondary text-sm font-medium mb-2">{exp.company}</div>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{exp.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;